import { useState } from "react";
import { Loader2, Sparkles, Zap, ScanFace, Users } from "lucide-react";
import { mediaUrl } from "../lib/api";

const POSITIONS = [
  { id: "top", label: "Top" },
  { id: "middle", label: "Middle" },
  { id: "bottom", label: "Bottom" },
];
const COLORS = ["#CCFF00", "#E83F3F", "#FF3EA5", "#00E5FF", "#FFFFFF"];
const DEFAULTS = { position: "bottom", color: "#CCFF00", size: 84, highlight: true, reactions: true, crop: 50, fill: false, reframe: false, active: false, smooth: true, pan_speed: "smooth" };

export const StyleControls = ({ clip, show, onApply, onLock, onNameFaces, onClearPreferred, saving, isVideo }) => {
  const init = { ...DEFAULTS, ...(clip.style || {}) };
  const [pos, setPos] = useState(init.position);
  const [color, setColor] = useState(init.color);
  const [size, setSize] = useState(init.size);
  const [highlight, setHighlight] = useState(init.highlight);
  const [reactions, setReactions] = useState(init.reactions);
  const [crop, setCrop] = useState(init.crop);
  const [fill, setFill] = useState(init.fill);
  const [reframe, setReframe] = useState(init.reframe);
  const [active, setActive] = useState(init.active);
  const [smooth, setSmooth] = useState(init.smooth);
  const [panSpeed, setPanSpeed] = useState(init.pan_speed || "smooth");

  const toggleActive = () => setActive((v) => { const n = !v; if (n) setReframe(false); return n; });
  const toggleReframe = () => setReframe((v) => { const n = !v; if (n) setActive(false); return n; });
  const locked = reframe || active;
  const track = clip.style?.track;
  const tiles = track?.tiles || [];
  const showPreview = active && clip.reframe_thumb && tiles.length > 0;
  const roster = show?.face_roster || [];
  const globalRoster = show?.global_roster || [];
  const matchName = (t) => {
    let best = "", bd = 0.08;
    [...roster, ...globalRoster].forEach((r) => { const d = Math.hypot(r.cx - t.cx, r.cy - t.cy); if (d < bd) { bd = d; best = r.name || ""; } });
    return best;
  };
  const [naming, setNaming] = useState(false);
  const [makeGlobal, setMakeGlobal] = useState(false);
  const [names, setNames] = useState(() => tiles.map(matchName));
  const [defaultTile, setDefaultTile] = useState(() => {
    const pf = show?.preferred_face; if (!pf) return -1;
    let best = -1, bd = 0.08;
    tiles.forEach((t, i) => { const d = Math.hypot(t.cx - pf.cx, t.cy - pf.cy); if (d < bd) { bd = d; best = i; } });
    return best;
  });
  const followName = track ? (names[track.primary] || track.preferred_name || "") : "";

  return (
    <div data-testid={`style-editor-${clip.id}`} className="space-y-4 p-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent">Caption Style</p>

      <div>
        <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Position</label>
        <div className="mt-1.5 grid grid-cols-3 gap-1.5">
          {POSITIONS.map((p) => (
            <button
              key={p.id}
              data-testid={`style-pos-${p.id}-${clip.id}`}
              onClick={() => setPos(p.id)}
              className={`py-2 text-xs uppercase tracking-widest transition-colors ${
                pos === p.id ? "bg-accent text-accent-foreground" : "bg-secondary text-muted-foreground hover:text-white"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Highlight Color</label>
        <div className="mt-1.5 flex gap-2">
          {COLORS.map((c) => (
            <button
              key={c}
              data-testid={`style-color-${c.replace("#", "")}-${clip.id}`}
              onClick={() => setColor(c)}
              className={`h-8 w-8 border-2 transition-transform hover:scale-110 ${color === c ? "border-white" : "border-transparent"}`}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Font Size</label>
          <span className="font-mono text-xs text-white">{size}</span>
        </div>
        <input
          data-testid={`style-size-${clip.id}`}
          type="range" min="56" max="120" step="2"
          value={size}
          onChange={(e) => setSize(parseInt(e.target.value))}
          className="mt-2 w-full accent-[#CCFF00]"
        />
      </div>

      <div className="flex gap-2">
        <button
          data-testid={`style-highlight-${clip.id}`}
          onClick={() => setHighlight((v) => !v)}
          className={`flex flex-1 items-center justify-center gap-1.5 py-2 text-xs uppercase tracking-widest transition-colors ${
            highlight ? "bg-accent/20 text-accent" : "bg-secondary text-muted-foreground"
          }`}
        >
          <Sparkles className="h-3.5 w-3.5" /> Karaoke
        </button>
        <button
          data-testid={`style-reactions-${clip.id}`}
          onClick={() => setReactions((v) => !v)}
          className={`flex flex-1 items-center justify-center gap-1.5 py-2 text-xs uppercase tracking-widest transition-colors ${
            reactions ? "bg-accent/20 text-accent" : "bg-secondary text-muted-foreground"
          }`}
        >
          <Zap className="h-3.5 w-3.5" /> Reactions
        </button>
      </div>

      {isVideo && (
        <div className="space-y-3 border border-accent/30 bg-accent/[0.04] p-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent">Footage Crop (video)</p>

          <button
            data-testid={`style-active-${clip.id}`}
            onClick={toggleActive}
            className={`flex w-full items-center justify-center gap-1.5 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors ${
              active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-white"
            }`}
          >
            <Users className="h-4 w-4" /> Active Speaker {active ? "· On" : "· Off"}
          </button>
          {active && (
            <>
              <p className="font-mono text-[10px] leading-relaxed text-primary/90">
                Dynamic follow on — the crop tracks whoever's talking and switches between people through the clip (uses lip-motion, falls back to the main face).
              </p>
              <button
                data-testid={`style-smooth-${clip.id}`}
                onClick={() => setSmooth((v) => !v)}
                className={`flex w-full items-center justify-center gap-1.5 py-2 text-[11px] uppercase tracking-widest transition-colors ${
                  smooth ? "bg-accent/20 text-accent" : "bg-secondary text-muted-foreground"
                }`}
              >
                {smooth ? "Smooth Pan (glide)" : "Hard Cut (snap)"}
              </button>
              {smooth && (
                <div className="flex gap-1">
                  {[["fast", "Snappy"], ["smooth", "Smooth"], ["cinematic", "Cinematic"]].map(([id, label]) => (
                    <button
                      key={id}
                      data-testid={`pan-speed-${id}-${clip.id}`}
                      onClick={() => setPanSpeed(id)}
                      className={`flex-1 py-1.5 text-[9px] uppercase tracking-widest transition-colors ${
                        panSpeed === id ? "bg-accent text-accent-foreground" : "bg-secondary text-muted-foreground hover:text-white"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              {showPreview && (
                <div className="space-y-1.5">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Following{followName ? `: ${followName}` : ""} — tap a face to lock the crop on someone else
                  </p>
                  <div data-testid={`reframe-preview-${clip.id}`} className="relative w-full overflow-hidden border border-border">
                    <img src={mediaUrl(clip.reframe_thumb)} alt="clip frame" className="block w-full" />
                    {tiles.map((t, i) => {
                      const primary = track.primary === i;
                      const nm = names[i] || matchName(t);
                      return (
                        <button
                          key={i}
                          data-testid={`reframe-tile-${i}-${clip.id}`}
                          onClick={() => onLock?.(i)}
                          disabled={saving}
                          title={primary ? "Currently followed" : "Lock crop on this person"}
                          className="group absolute box-border flex items-start justify-start border-2 transition-colors hover:border-white"
                          style={{
                            left: `${t.cx * 100}%`,
                            top: `${t.cy * 100}%`,
                            width: `${t.w * 100}%`,
                            height: `${t.h * 100}%`,
                            minWidth: "26px",
                            minHeight: "26px",
                            transform: "translate(-50%, -50%)",
                            borderColor: primary ? "#CCFF00" : "rgba(255,255,255,0.5)",
                            boxShadow: primary ? "0 0 12px rgba(204,255,0,0.5)" : "none",
                          }}
                        >
                          {(primary || nm) && (
                            <span className={`absolute -top-[14px] left-0 whitespace-nowrap px-1 font-mono text-[8px] font-bold uppercase ${primary ? "bg-accent text-black" : "bg-black/70 text-white"}`}>
                              {nm || "Following"}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    data-testid={`name-faces-toggle-${clip.id}`}
                    onClick={() => setNaming((v) => !v)}
                    className="w-full bg-secondary py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-white"
                  >
                    {naming ? "Hide naming" : "Name the faces (saved for this show)"}
                  </button>
                  {naming && (
                    <div className="space-y-2 border border-border bg-background p-2">
                      {tiles.map((t, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <input
                            data-testid={`face-name-${i}-${clip.id}`}
                            value={names[i] || ""}
                            onChange={(e) => { const n = [...names]; n[i] = e.target.value; setNames(n); }}
                            placeholder={`Face ${i + 1}`}
                            className="min-w-0 flex-1 border border-border bg-secondary px-2 py-1.5 font-mono text-xs text-white outline-none focus:border-accent"
                          />
                          <button
                            data-testid={`face-default-${i}-${clip.id}`}
                            onClick={() => setDefaultTile((d) => (d === i ? -1 : i))}
                            title="Follow this person in every clip"
                            className={`px-2 py-1.5 text-sm transition-colors ${defaultTile === i ? "bg-accent text-black" : "bg-secondary text-muted-foreground hover:text-white"}`}
                          >
                            ★
                          </button>
                        </div>
                      ))}
                      <button
                        data-testid={`save-faces-${clip.id}`}
                        onClick={() => onNameFaces?.({
                          faces: tiles.map((t, i) => ({ cx: t.cx, cy: t.cy, name: names[i] || "" })),
                          prefer: defaultTile >= 0 ? { cx: tiles[defaultTile].cx, cy: tiles[defaultTile].cy, name: names[defaultTile] || "" } : null,
                          makeGlobal: defaultTile >= 0 && makeGlobal,
                        })}
                        disabled={saving}
                        className="w-full bg-primary py-2 text-[11px] font-semibold uppercase tracking-widest text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                      >
                        {defaultTile >= 0 ? "Save & follow ★ in every clip" : "Save names"}
                      </button>
                      {defaultTile >= 0 && (
                        <label className="flex cursor-pointer items-center gap-2 pt-1 font-mono text-[10px] text-muted-foreground">
                          <input
                            data-testid={`make-global-${clip.id}`}
                            type="checkbox"
                            checked={makeGlobal}
                            onChange={(e) => setMakeGlobal(e.target.checked)}
                            className="accent-[#CCFF00]"
                          />
                          Also follow {names[defaultTile] || "them"} in all my future shows
                        </label>
                      )}
                    </div>
                  )}
                  {show?.preferred_face && (
                    <button
                      data-testid={`clear-preferred-${clip.id}`}
                      onClick={() => onClearPreferred?.()}
                      disabled={saving}
                      className="w-full border border-border py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-white disabled:opacity-50"
                    >
                      Back to auto-follow (dynamic) — drop {show.preferred_face.name || "the pin"}
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          <button
            data-testid={`style-reframe-${clip.id}`}
            onClick={toggleReframe}
            className={`flex w-full items-center justify-center gap-1.5 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors ${
              reframe ? "bg-accent text-accent-foreground" : "bg-secondary text-muted-foreground hover:text-white"
            }`}
          >
            <ScanFace className="h-4 w-4" /> Smart Reframe {reframe ? "· On" : "· Off"}
          </button>
          {reframe ? (
            <p className="font-mono text-[10px] leading-relaxed text-accent/80">
              Auto face-tracking on — Barth is detected and locked centered for the whole clip. Manual crop below is ignored.
            </p>
          ) : !active ? (
            <p className="font-mono text-[10px] leading-relaxed text-muted-foreground/70">
              Smart Reframe locks on one face; Active Speaker follows the conversation. Great for wide or multi-person footage.
            </p>
          ) : null}

          <div className={locked ? "pointer-events-none opacity-40" : ""}>
            <div className="flex items-center justify-between">
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Vertical focus</label>
              <span className="font-mono text-xs text-white">{crop <= 33 ? "Top" : crop >= 67 ? "Bottom" : "Center"}</span>
            </div>
            <input
              data-testid={`style-crop-${clip.id}`}
              type="range" min="0" max="100" step="5"
              value={crop}
              onChange={(e) => setCrop(parseInt(e.target.value))}
              className="mt-2 w-full accent-[#CCFF00]"
            />
          </div>
          <button
            data-testid={`style-fill-${clip.id}`}
            onClick={() => setFill((v) => !v)}
            disabled={locked}
            className={`flex w-full items-center justify-center gap-1.5 py-2 text-xs uppercase tracking-widest transition-colors disabled:opacity-40 ${
              fill ? "bg-accent/20 text-accent" : "bg-secondary text-muted-foreground"
            }`}
          >
            {fill ? "Fill Frame (zoomed)" : "Fit + Blurred Bars"}
          </button>
        </div>
      )}

      <button
        data-testid={`style-apply-${clip.id}`}
        onClick={() => onApply({ position: pos, color, size, highlight, reactions, crop, fill, reframe, active, smooth, pan_speed: panSpeed })}
        disabled={saving}
        className="flex w-full items-center justify-center gap-2 bg-primary py-2.5 text-xs font-semibold uppercase tracking-widest text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
        {saving ? "Rendering video" : "Apply & Re-render"}
      </button>
    </div>
  );
};
